import { FC } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { DropdownMenu } from 'radix-ui';
import { Plus, GraduationCap, BookOpen, FileText, Target, LucideIcon } from 'lucide-react';
import { cn } from '@new/lib';
import { Button } from '@new/common/ui/Button/Button';

const ICON_SIZE = 18;
const ICON_STROKE_WIDTH = 1.5;

const DROPDOWN_ITEM_BASE = cn(
  'flex items-center gap-3 px-3 py-3 rounded-md',
  'text-(--neutral-black-NB600) hover:bg-(--primary-O200) hover:text-(--primary-O900)',
  'cursor-pointer outline-none transition-all duration-200',
  'no-underline',
  'data-[highlighted]:bg-(--neutral-black-NB100) data-[highlighted]:text-(--primary-O900)'
);

const DROPDOWN_CONTENT_CLASS = cn(
  'min-w-[200px] bg-(--neutral-white-NW1000) rounded-2xl shadow-sm!',
  'border border-(--neutral-black-NB100)',
  'z-50',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
  'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
);

interface CreateItem {
  label: string;
  icon: LucideIcon;
  path: string;
}

const CREATE_ITEMS: CreateItem[] = [
  {
    label: 'Degree',
    icon: GraduationCap,
    path: '/new-degree',
  },
  {
    label: 'Course',
    icon: BookOpen,
    path: '/new-course',
  },
  {
    label: 'Lesson',
    icon: FileText,
    path: '/new-lesson',
  },
  {
    label: 'Objective',
    icon: Target,
    path: '/new-objective',
  },
];

export const CreateDropdown: FC = () => {
  const navigate = useNavigate();

  const onSelect = (item: CreateItem) => {
    navigate({ to: item.path });
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button variant="primary" className="h-8! w-8! p-0! min-w-0! rounded-md!">
          <Plus size={16} />
        </Button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content className={DROPDOWN_CONTENT_CLASS} sideOffset={5} align="end">
          {CREATE_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <DropdownMenu.Item
                key={item.label}
                onSelect={() => onSelect(item)}
                className={DROPDOWN_ITEM_BASE}
              >
                <Icon size={ICON_SIZE} strokeWidth={ICON_STROKE_WIDTH} />
                <span>{item.label}</span>
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
